'use client';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { getQuizByIdService, getQuizListService } from '@/services/httpServices/QuizServices';
import type { GetQuizByIdServiceConfig, QuizByIdResponse, QuizListResponse, QuizListServiceConfig } from '@/services/httpServices/QuizServices/types';

import { buildStandardPaginatedParams } from './createInfiniteQuery';
import { infiniteQuizListKeys, quizByIdKeys, quizListKeys } from './quizQueryKeys';
import { useQueryConfig } from './useQueryConfig';

export function useQuizQuery(params: QuizListServiceConfig, enabled: boolean = true) {
  const { isEnabled } = useQueryConfig();

  return useQuery<QuizListResponse>({
    queryKey: quizListKeys(params),
    queryFn: async () => getQuizListService(params),
    enabled: isEnabled && enabled,
  });
}

/**
 * Hook to fetch quiz list with infinite scrolling using cursor-based pagination
 * @param initialParams - Initial quiz list query parameters
 * @param enabled - Whether to enable the query
 * @returns Infinite query result for the quiz list
 */
export function useInfiniteQuizQuery(
  initialParams: QuizListServiceConfig,
  enabled: boolean = true,
) {
  const { isEnabled } = useQueryConfig();

  return useInfiniteQuery<QuizListResponse>({
    queryKey: infiniteQuizListKeys(initialParams),
    queryFn: async ({ pageParam }) => {
      const paginationParams = buildStandardPaginatedParams(
        initialParams,
        pageParam ? String(pageParam) : undefined,
      );
      return getQuizListService(paginationParams);
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      if (lastPage.pageInfo.hasNextPage === true && lastPage.pageInfo.endCursor !== null) {
        return lastPage.pageInfo.endCursor;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.pageInfo.hasPreviousPage === true && firstPage.pageInfo.startCursor !== null) {
        return firstPage.pageInfo.startCursor;
      }
      return undefined;
    },
    enabled: isEnabled && enabled,
  });
}

/**
 * Hook to fetch quiz data by ID
 * @param params - Quiz query parameters (optional)
 * @param enabled - Whether to enable the query (optional)
 * @returns Query result for the quiz
 */
export function useQuizByIdQuery(
  params?: GetQuizByIdServiceConfig,
  enabled?: boolean,
) {
  const { isEnabled } = useQueryConfig();

  return useQuery<QuizByIdResponse>({
    queryKey: quizByIdKeys(params),
    queryFn: async () => {
      if (params === undefined) {
        throw new Error('Quiz params are required');
      }
      return getQuizByIdService(params);
    },
    enabled: isEnabled && (enabled ?? (params !== undefined && params.params?.params.id !== undefined)),
    refetchOnWindowFocus: 'always',
  });
}
